// @flow

import * as React from 'react';
import { bind } from '@ta/common.api';
import { featureIsEnabled } from '@ta/common.features';
import { localize, unescaped } from '@ta/common.i18n';
import { ReadMore } from '@ta/common.text';
import classNames from '@ta/common.classnames';
import { TabActiveContext } from '@ta/social.tabs';
import { Icon } from '@ta/styleguide.icon';
import { InteractionLogger } from '@ta/tracking.interactions';

import GeoOverviewPhotos from './geoOverviewExtraParts/GeoOverviewPhotos';
import GeoOverviewQuickLinks from './geoOverviewExtraParts/GeoOverviewQuickLinks';
import styles from './GeoOverviewSection.less';

type Props = {
  /** The title value from OPF, getting from API call */
  opfTitle: ?string,

  overview: any,
  targetGeoId: any,
};

const getOverviewTitle = (geo: any, opfTitle: ?string) => {
  if (opfTitle) {
    return opfTitle;
  }

  /* Overview */
  return localize('hr_tabs_overview_tab_fffff660');
};

const GeoOverviewSection = ({ overview, opfTitle, targetGeoId }: Props) => {
  if (!overview || !overview.object) {
    return null;
  }

  const geoAlbum =
    overview.object.geo && overview.object.geo.albums && overview.object.geo.albums.length > 0
      ? overview.object.geo.albums[0]
      : null;
  const sponsoredPhotos = overview.object.geo ? overview.object.geo.dmoPhotos : null;

  const singleSearchMobileWebVariants1Or2 = featureIsEnabled(
    'cx_single_search_var_1_or_2_mobile_web',
  );

  const readMoreClass = classNames(styles.readMore, {
    [styles.grayButton]: singleSearchMobileWebVariants1Or2,
  });
  
  const geoArr = [293738,29750,34145,40424,43686,45086,662290,295398,39588,48562,33845,187229,187481,294205,658842,664891,294066,42512,45963,49785,293817,147347,35516,187080,227947,293767,45964,45992,45961,659474,155987,147399,147247,147417];

  return (
    <div className={`${styles.section} ui_card`}>
      <div className={styles.header}>
        <h1 id="HEADING">
          <span className="ui_header h2">{getOverviewTitle(overview.object.geo, opfTitle)} </span>
        </h1>
      </div>
      {singleSearchMobileWebVariants1Or2 && overview.object.geo.orderedQuickLinksV2 && (
        <GeoOverviewQuickLinks orderedQuickLinks={overview.object.geo.orderedQuickLinksV2} />
      )}
      {overview.object.geoOverview &&  !geoArr.includes(targetGeoId) && (
        <React.Fragment>
          <div className={`${styles.container} ui_container`}>
            <InteractionLogger>
              {trackFeedClick => (
                <TabActiveContext.Consumer>
                  {tabData => (
                    <ReadMore
                      numLinesToShow={singleSearchMobileWebVariants1Or2 ? 3 : 4}
                      collapsible
                      ctaNewline
                      collapsed={tabData ? tabData.isActive : true}
                      cta={
                        <React.Fragment>
                          <span className={styles.ellipsis} />
                          <div
                            className={readMoreClass}
                            onClick={e => trackFeedClick(e, 'feedClick')}
                          >
                            <span>{localize('get_to_know_geo_show_more2') /* Show more */}</span>
                            <Icon name="caret-down" className={styles.icon} />
                          </div>
                        </React.Fragment>
                      }
                      ctaCollapse={
                        <div className={readMoreClass}>
                          <span>{localize('get_to_know_geo_show_less') /* Show less */}</span>
                          <Icon name="caret-up" className={styles.icon} />
                        </div>
                      }
                      key={overview.object.id}
                    >
                      <span>{unescaped(overview.object.geoOverview)}</span>
                    </ReadMore>
                  )}
                </TabActiveContext.Consumer>
              )}
            </InteractionLogger>
          </div>
        </React.Fragment>
      )}
      {geoAlbum ? (
        <GeoOverviewPhotos
          {...geoAlbum}
          photos={[...(sponsoredPhotos || []), ...(geoAlbum.photos || [])]}
        />
      ) : null}
    </div>
  );
};

export default bind<Props, {||}>((state, stage) => {
  const geoId = state.route.isNearby ? state.page.geoId : state.route.geo;
  const endpoint = geoId
    ? stage(`/data/1.0/onpagefactors/basic?servletName=Tourism&locationId=${geoId}`)
    : null;

  return () => {
    const data = endpoint && endpoint.get().data;
    return {
      opfTitle: data?.mainH1 || '',
      targetGeoId: geoId
    };
  };
})(GeoOverviewSection);
